import React, { useState, useEffect } from "react";

const ProfitCalculator = () => {
  const [revenue, setRevenue] = useState(500000);
  const [cogs, setCogs] = useState(200000);
  const [operatingExpenses, setOperatingExpenses] = useState(100000);
  const [taxRate, setTaxRate] = useState(25);
  const [otherIncome, setOtherIncome] = useState(0);
  const [otherExpenses, setOtherExpenses] = useState(0);
  const [result, setResult] = useState({
    grossProfit: "0.00",
    grossProfitMargin: "0.00",
    netProfit: "0.00",
    netProfitMargin: "0.00",
    ebitda: "0.00",
    ebitdaMargin: "0.00",
    totalTax: "0.00",
    profitBeforeTax: "0.00",
  });

  // Profit Calculation Function
  const calculateProfit = () => {
    const rev = parseFloat(revenue);
    const cogsVal = parseFloat(cogs);
    const opex = parseFloat(operatingExpenses);
    const tax = parseFloat(taxRate) / 100;
    const otherInc = parseFloat(otherIncome) || 0;
    const otherExp = parseFloat(otherExpenses) || 0;

    if (isNaN(rev) || isNaN(cogsVal) || rev < 0 || cogsVal < 0) return;

    // Calculate Gross Profit
    const grossProfit = rev - cogsVal;
    const grossProfitMargin = rev > 0 ? (grossProfit / rev) * 100 : 0;

    // Calculate EBITDA
    const ebitda = grossProfit - opex;
    const ebitdaMargin = rev > 0 ? (ebitda / rev) * 100 : 0;

    // Calculate Profit Before Tax
    const profitBeforeTax = ebitda + otherInc - otherExp;

    // Calculate Tax
    const totalTax = profitBeforeTax > 0 ? profitBeforeTax * tax : 0;

    // Calculate Net Profit
    const netProfit = profitBeforeTax - totalTax;
    const netProfitMargin = rev > 0 ? (netProfit / rev) * 100 : 0;

    setResult({
      grossProfit: grossProfit.toFixed(2),
      grossProfitMargin: grossProfitMargin.toFixed(2),
      netProfit: netProfit.toFixed(2),
      netProfitMargin: netProfitMargin.toFixed(2),
      ebitda: ebitda.toFixed(2),
      ebitdaMargin: ebitdaMargin.toFixed(2),
      totalTax: totalTax.toFixed(2),
      profitBeforeTax: profitBeforeTax.toFixed(2),
    });
  };

  useEffect(() => {
  calculateProfit();
}, [revenue, cogs, operatingExpenses, taxRate, otherIncome, otherExpenses]);


  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
          <h1>Gross/Net Profit Calculator</h1>
          <p>
            Calculate your business profitability metrics including gross profit, net profit, and profit margins
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                Financial Details
              </h2>

              {/* Revenue */}
              <div className="input-group">
                <label>Total Revenue (Sales)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={revenue}
                    onChange={(e) => setRevenue(e.target.value)}
                    className="calc-input"
                    placeholder="500000"
                  />
                </div>
                <div className="quick-select-group">
                  {[100000, 500000, 1000000, 5000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setRevenue(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 100000 ? (amount / 100000).toFixed(0) + 'L' : (amount / 1000).toFixed(0) + 'K'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cost of Goods Sold */}
              <div className="input-group">
                <label>Cost of Goods Sold (COGS)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={cogs}
                    onChange={(e) => setCogs(e.target.value)}
                    className="calc-input"
                    placeholder="200000"
                  />
                </div>
                <div className="quick-select-group">
                  {[50000, 100000, 200000, 500000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setCogs(amount)}
                      className="quick-select-btn secondary"
                    >
                      ₹{amount >= 100000 ? (amount / 100000).toFixed(0) + 'L' : (amount / 1000).toFixed(0) + 'K'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Operating Expenses */}
              <div className="input-group">
                <label>Operating Expenses (OPEX)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={operatingExpenses}
                    onChange={(e) => setOperatingExpenses(e.target.value)}
                    className="calc-input"
                    placeholder="100000"
                  />
                </div>
                <div className="quick-select-group">
                  {[25000, 50000, 100000, 250000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setOperatingExpenses(amount)}
                      className="quick-select-btn success"
                    >
                      ₹{amount >= 100000 ? (amount / 100000).toFixed(0) + 'L' : (amount / 1000).toFixed(0) + 'K'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Other Income */}
              <div className="input-group">
                <label>Other Income (Optional)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={otherIncome}
                    onChange={(e) => setOtherIncome(e.target.value)}
                    className="calc-input"
                    placeholder="0"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 10000, 25000, 50000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setOtherIncome(amount)}
                      className="quick-select-btn primary"
                    >
                      {amount === 0 ? 'None' : '₹' + (amount / 1000).toFixed(0) + 'K'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Other Expenses */}
              <div className="input-group">
                <label>Other Expenses (Optional)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={otherExpenses}
                    onChange={(e) => setOtherExpenses(e.target.value)}
                    className="calc-input"
                    placeholder="0"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 10000, 25000, 50000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setOtherExpenses(amount)}
                      className="quick-select-btn secondary"
                    >
                      {amount === 0 ? 'None' : '₹' + (amount / 1000).toFixed(0) + 'K'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tax Rate */}
              <div className="input-group">
                <label>Tax Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                    className="calc-input"
                    placeholder="25"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[18, 25, 30, 35].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setTaxRate(rate)}
                      className="quick-select-btn success"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateProfit} className="calculate-btn">
                Calculate Profit
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Profit Analysis
              </h2>

              <div className="result-card">
                {/* Net Profit - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Net Profit (After Tax)</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.netProfit).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.netProfitMargin}% net profit margin
                  </div>
                </div>

                {/* Gross Profit */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Gross Profit</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.grossProfit).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.grossProfitMargin}% gross margin
                  </div>
                </div>

                {/* EBITDA */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">EBITDA</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.ebitda).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.ebitdaMargin}% EBITDA margin
                  </div>
                </div>

                {/* Profit Before Tax */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Profit Before Tax</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.profitBeforeTax).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Tax: ₹{Number(result.totalTax).toLocaleString("en-IN")}
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Profit Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Gross Profit Margin</span>
                      <span className="breakdown-percentage">
                        {result.grossProfitMargin}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${Math.min(parseFloat(result.grossProfitMargin), 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Net Profit Margin</span>
                      <span className="breakdown-percentage">
                        {result.netProfitMargin}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${Math.min(parseFloat(result.netProfitMargin), 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Gross and Net Profit</h3>
              <p>
                Gross profit and net profit are crucial profitability metrics that help you understand your business's 
                financial health. Gross profit is calculated by subtracting the cost of goods sold (COGS) from total 
                revenue, showing how efficiently you produce or source your products. Net profit, on the other hand, 
                is what remains after deducting all expenses, including operating costs, taxes, and other expenses 
                from revenue. This calculator provides a comprehensive view of your profitability by calculating both 
                gross and net profit margins, along with EBITDA (Earnings Before Interest, Tax, Depreciation, and 
                Amortization), which is widely used to assess operational performance independent of capital structure 
                and tax impacts.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Profit Calculation Formulas:</h4>
            <div className="formula-display">
              Gross Profit = Revenue - COGS
            </div>
            <div className="formula-display">
              Net Profit = (Revenue - COGS - OPEX + Other Income - Other Expenses) × (1 - Tax Rate)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Revenue</div>
                <div className="formula-param-value">Total sales or income generated</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">COGS</div>
                <div className="formula-param-value">Direct costs of producing goods/services</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">OPEX</div>
                <div className="formula-param-value">Operating expenses (rent, salaries, utilities)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Profit Margin</div>
                <div className="formula-param-value">(Profit / Revenue) × 100</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is the difference between gross profit and net profit?</h5>
              <p>
                Gross profit is revenue minus direct production costs (COGS), showing production efficiency. Net profit is the final amount after deducting all expenses including operating costs, taxes, and other expenses. Gross profit indicates how well you manage production costs, while net profit reveals overall business profitability and sustainability.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is a good profit margin for a business?</h5>
              <p>
                Good profit margins vary by industry. Generally, 50-70% gross margin is healthy for product businesses, while 10-20% net margin is considered good. However, retail may operate on 2-5% net margins, while software companies can achieve 20-30%. Always compare your margins to industry benchmarks and focus on improving them over time.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is EBITDA and why is it important?</h5>
              <p>
                EBITDA (Earnings Before Interest, Tax, Depreciation, Amortization) measures operational performance by excluding financing decisions, taxes, and non-cash expenses. It allows easier comparison between businesses with different capital structures and tax situations. Investors use it to assess core business profitability and cash generation capability.
              </p>
            </div>

            <div className="faq-item">
              <h5>How can I improve my business's profit margins?</h5>
              <p>
                Improve margins by reducing costs and increasing revenue. For gross profit, negotiate better supplier prices, reduce waste, and optimize production. For net profit, control operating expenses through automation and efficiency. On the revenue side, focus on value-based pricing, upselling, and targeting higher-margin products or customer segments.
              </p>
            </div>

            <div className="faq-item">
              <h5>What expenses are included in Cost of Goods Sold (COGS)?</h5>
              <p>
                COGS includes all direct costs to produce or purchase goods sold. For manufacturers: raw materials, direct labor, and manufacturing overhead. For retailers: purchase price plus freight. For services: direct labor costs. COGS does NOT include indirect costs like marketing, rent, or admin expenses - those are operating expenses.
              </p>
            </div>

            <div className="faq-item">
              <h5>Should I focus on increasing revenue or reducing costs?</h5>
              <p>
                Both are important, but priorities depend on your situation. Cost reduction provides quick, predictable results with lower risk - ideal for thin margins or downturns. Revenue growth offers unlimited upside but requires investment. Best approach: optimize costs continuously while investing in high-ROI growth initiatives. Balance both strategies for sustainable success.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfitCalculator;